/**
 * EventManager - Utility for managing event listener lifecycle in Web Components
 *
 * Provides:
 * - Automatic tracking of registered event listeners
 * - Cleanup on component disconnect
 * - Common patterns: escape to close, click outside, etc.
 * - Event delegation helpers
 *
 * Usage Pattern 1 - Mixin style (recommended):
 *   class MyComponent extends EventManagerMixin(HTMLElement) {
 *     connectedCallback() {
 *       // Listeners are automatically tracked and cleaned up
 *       this.addManagedListener(document, 'keydown', this.handleKeydown);
 *       this.addEscapeToClose(() => this.close());
 *     }
 *   }
 *
 * Usage Pattern 2 - Composition style:
 *   class MyComponent extends HTMLElement {
 *     constructor() {
 *       super();
 *       this._eventManager = new EventManager(this);
 *     }
 *     disconnectedCallback() {
 *       this._eventManager.cleanup();
 *     }
 *   }
 */

/**
 * EventManager class for tracking and cleaning up event listeners
 */
export class EventManager {
    /**
     * @param {HTMLElement} component - The component instance
     */
    constructor(component) {
        this.component = component;
        this._listeners = [];
        this._delegatedListeners = new Map();
    }

    /**
     * Add a managed event listener that will be automatically cleaned up
     *
     * @param {EventTarget} target - Element to attach listener to
     * @param {string} eventType - Event type (e.g., 'click', 'keydown')
     * @param {Function} handler - Event handler function
     * @param {Object} [options] - addEventListener options
     * @returns {Function} Cleanup function to remove this specific listener
     */
    addListener(target, eventType, handler, options = {}) {
        // Bind handler to component if not already bound
        const boundHandler = handler.bind(this.component);

        target.addEventListener(eventType, boundHandler, options);

        const listenerInfo = { target, eventType, handler: boundHandler, options };
        this._listeners.push(listenerInfo);

        // Return cleanup function for manual removal if needed
        return () => this.removeListener(listenerInfo);
    }

    /**
     * Remove a specific listener
     * @param {Object} listenerInfo - Listener info object from addListener
     */
    removeListener(listenerInfo) {
        const { target, eventType, handler, options } = listenerInfo;
        target.removeEventListener(eventType, handler, options);

        const index = this._listeners.indexOf(listenerInfo);
        if (index > -1) {
            this._listeners.splice(index, 1);
        }
    }

    /**
     * Add event delegation listener for dynamic content
     * Uses a single listener on a parent element to handle events on children
     *
     * @param {Element} container - Parent element to attach listener to
     * @param {string} eventType - Event type (e.g., 'click')
     * @param {string} selector - CSS selector for target elements
     * @param {Function} handler - Handler function, receives (event, matchedElement)
     * @returns {Function} Cleanup function
     */
    addDelegatedListener(container, eventType, selector, handler) {
        const boundHandler = (e) => {
            const matchedElement = e.target.closest(selector);
            if (matchedElement && container.contains(matchedElement)) {
                handler.call(this.component, e, matchedElement);
            }
        };

        container.addEventListener(eventType, boundHandler);

        const key = `${eventType}:${selector}`;
        this._delegatedListeners.set(key, { container, eventType, handler: boundHandler });

        return () => {
            container.removeEventListener(eventType, boundHandler);
            this._delegatedListeners.delete(key);
        };
    }

    /**
     * Add Escape key handler to close a dialog/modal
     * Only triggers when component has 'open' attribute
     *
     * @param {Function} closeCallback - Function to call when Escape is pressed
     * @returns {Function} Cleanup function
     */
    addEscapeToClose(closeCallback) {
        const handler = (e) => {
            if (e.key === 'Escape' && this.component.hasAttribute('open')) {
                e.preventDefault();
                closeCallback.call(this.component);
            }
        };

        return this.addListener(document, 'keydown', handler);
    }

    /**
     * Add click-outside handler to close a dialog/modal
     * Triggers when clicking the backdrop (the component itself, not its children)
     *
     * @param {Function} closeCallback - Function to call when clicking outside
     * @returns {Function} Cleanup function
     */
    addClickOutsideToClose(closeCallback) {
        const handler = (e) => {
            // Only close if clicking directly on the host element (backdrop)
            if (e.target === this.component) {
                closeCallback.call(this.component);
            }
        };

        return this.addListener(this.component, 'click', handler);
    }

    /**
     * Add click handler for a close button inside Shadow DOM
     *
     * @param {string} selector - CSS selector for close button(s)
     * @param {Function} closeCallback - Function to call when clicked
     * @returns {Function} Cleanup function
     */
    addCloseButton(selector, closeCallback) {
        const shadowRoot = this.component.shadowRoot;
        if (!shadowRoot) {
            console.warn('EventManager: Component has no shadowRoot');
            return () => {};
        }

        const buttons = shadowRoot.querySelectorAll(selector);
        const cleanupFunctions = [];

        buttons.forEach(btn => {
            const handler = () => closeCallback.call(this.component);
            btn.addEventListener('click', handler);
            cleanupFunctions.push(() => btn.removeEventListener('click', handler));
        });

        return () => cleanupFunctions.forEach(fn => fn());
    }

    /**
     * Clean up all managed listeners
     * Call this in disconnectedCallback
     */
    cleanup() {
        // Remove all regular listeners
        this._listeners.forEach(({ target, eventType, handler, options }) => {
            target.removeEventListener(eventType, handler, options);
        });
        this._listeners = [];

        // Remove all delegated listeners
        this._delegatedListeners.forEach(({ container, eventType, handler }) => {
            container.removeEventListener(eventType, handler);
        });
        this._delegatedListeners.clear();
    }

    /**
     * Get count of active listeners (for debugging)
     * @returns {number} Number of active listeners
     */
    get listenerCount() {
        return this._listeners.length + this._delegatedListeners.size;
    }
}

/**
 * Mixin factory for adding EventManager capabilities to a component class
 *
 * @param {typeof HTMLElement} Base - Base class to extend
 * @returns {typeof HTMLElement} Extended class with EventManager methods
 *
 * @example
 * class MyDialog extends EventManagerMixin(HTMLElement) {
 *   connectedCallback() {
 *     this.addEscapeToClose(() => this.close());
 *     this.addClickOutsideToClose(() => this.close());
 *   }
 * }
 */
export function EventManagerMixin(Base) {
    return class extends Base {
        constructor(...args) {
            super(...args);
            this._eventManager = new EventManager(this);
        }

        /**
         * Clean up all managed listeners
         * Override disconnectedCallback but call super if it exists
         */
        disconnectedCallback() {
            this._eventManager.cleanup();
            if (super.disconnectedCallback) {
                super.disconnectedCallback();
            }
        }

        /**
         * Add a managed event listener
         * @see EventManager.addListener
         */
        addManagedListener(target, eventType, handler, options) {
            return this._eventManager.addListener(target, eventType, handler, options);
        }

        /**
         * Add event delegation listener
         * @see EventManager.addDelegatedListener
         */
        addDelegatedListener(container, eventType, selector, handler) {
            return this._eventManager.addDelegatedListener(container, eventType, selector, handler);
        }

        /**
         * Add Escape key to close handler
         * @see EventManager.addEscapeToClose
         */
        addEscapeToClose(closeCallback) {
            return this._eventManager.addEscapeToClose(closeCallback);
        }

        /**
         * Add click outside to close handler
         * @see EventManager.addClickOutsideToClose
         */
        addClickOutsideToClose(closeCallback) {
            return this._eventManager.addClickOutsideToClose(closeCallback);
        }

        /**
         * Add close button handler
         * @see EventManager.addCloseButton
         */
        addCloseButton(selector, closeCallback) {
            return this._eventManager.addCloseButton(selector, closeCallback);
        }

        /**
         * Clean up all managed listeners manually
         */
        cleanupListeners() {
            this._eventManager.cleanup();
        }

        /**
         * Get count of active listeners (for debugging)
         */
        get managedListenerCount() {
            return this._eventManager.listenerCount;
        }
    };
}

// Default export
export default {
    EventManager,
    EventManagerMixin
};
